import React, { useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Button } from "reactstrap";

export default function UnitReportExportModal({
  isOpen,
  title = "Exportar dados",
  fields = [],
  fileName = "",
  groupOptions = [],
  groupSelection = "",
  error = "",
  onClose,
  onConfirm,
  onFileNameChange,
  onGroupChange,
  onToggleField,
}) {
  const hasFields = Array.isArray(fields) && fields.length > 0;
  const selectedCount = useMemo(() => (hasFields ? fields.filter((f) => f.selected).length : 0), [hasFields, fields]);
  const canExport = hasFields && selectedCount > 0;

  const activeGroupValue = useMemo(() => {
    const hasOptions = Array.isArray(groupOptions) && groupOptions.length > 0;
    if (!hasOptions) return "";
    if (groupOptions.some((o) => o.value === groupSelection)) return groupSelection;
    const firstEnabled = groupOptions.find((o) => !o.disabled);
    return firstEnabled ? firstEnabled.value : groupSelection;
  }, [groupOptions, groupSelection]);

  const fieldGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "0.75rem",
  };
  const fieldItemStyle = {
    border: "1px solid #edf2f9",
    borderRadius: "0.5rem",
    backgroundColor: "#f8fbff",
    padding: "0.65rem 0.75rem",
    display: "flex",
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 34, 58, 0.08)",
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{title || "Exportar dados"}</ModalHeader>
      <ModalBody className="py-4">
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="text-sm text-muted">Nome do arquivo</div>
            <span className="badge badge-light text-muted">.csv</span>
          </div>
          <Input type="text" value={fileName} onChange={onFileNameChange} placeholder="relatorio-personalizado.csv" />
          <div className="small text-muted mt-2">
            Sugerimos usar apenas letras, números, hífen e underline para evitar problemas na hora de compartilhar o arquivo.
          </div>
        </div>

        {Array.isArray(groupOptions) && groupOptions.length > 0 ? (
          <div className="mb-4">
            <div className="text-sm text-muted mb-2">Escopo da exportação</div>
            <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
              {groupOptions.map((option) => {
                const isActive = option.value === activeGroupValue;
                return (
                  <Button
                    key={option.value}
                    size="sm"
                    color={isActive ? "primary" : "secondary"}
                    outline={!isActive}
                    onClick={() => onGroupChange && onGroupChange(option.value)}
                    disabled={Boolean(option.disabled)}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
            <div className="small text-muted mt-2">Escolha quais usuários entram na planilha antes de exportar.</div>
          </div>
        ) : null}

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="text-sm text-muted">Campos disponíveis</div>
              <div className="font-weight-medium">Escolha o que vai para a planilha</div>
            </div>
            {hasFields ? (
              <div className="small text-muted text-right">
                {selectedCount} de {fields.length} selecionados
              </div>
            ) : null}
          </div>
          {hasFields ? (
            <div style={fieldGridStyle}>
              {fields.map((field) => (
                <div key={field.key} style={fieldItemStyle} className="mb-0">
                  <div className="form-check" style={{ display: "flex", alignItems: "center", gap: "0.75rem", width: "100%" }}>
                    <Input
                      type="checkbox"
                      id={`export-field-${field.key}`}
                      checked={Boolean(field.selected)}
                      onChange={() => onToggleField && onToggleField(field.key)}
                      className="form-check-input"
                      style={{ flex: "0 0 auto" }}
                    />
                    <label htmlFor={`export-field-${field.key}`} className="form-check-label mb-0 text-sm" style={{ cursor: "pointer", flex: "1 1 auto" }}>
                      {field.label} {field.count !== undefined ? <span className="text-muted small">({field.count})</span> : null}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">Nenhum campo disponível para exportação.</div>
          )}
        </div>

        {error ? <div className="alert alert-warning py-2 px-3 text-sm mb-0">{error}</div> : null}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <div className="small text-muted">{canExport ? "Pronto para exportar" : "Selecione pelo menos um campo"}</div>
        <div>
          <Button color="secondary" onClick={onClose} className="mr-2">
            Cancelar
          </Button>
          <Button color="primary" onClick={onConfirm} disabled={!canExport}>
            Exportar CSV
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
